import {createStyles} from '@mantine/core';
import React, {useCallback} from 'react';

import navigateToURL from 'lib/navigateToURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import SidebarRow from './SidebarRow';

const useStyles = createStyles(() => ({
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
}));

export interface ChatSidebarNavProps {
  /** Start a new chat (mirrors the header "New chat" action). */
  onNewChat: () => void;
}

function NavIcon({icon}: {icon: BenchlingIconName}): JSX.Element {
  return <BenchlingIcon icon={icon} />;
}

/**
 * Primary navigation for the redesigned chat sidebar. "New Chat" resets to the welcome panel; the
 * remaining destinations open the chat settings modal on their respective tab. "Artifacts" is
 * planned but not yet implemented, so it is intentionally omitted for now.
 */
export default function ChatSidebarNav({onNewChat}: ChatSidebarNavProps): JSX.Element {
  const {classes} = useStyles();

  const openSettings = useCallback((tab: ChatSettingsTab) => {
    navigateToURL(chatSettingsModalUrl(tab));
  }, []);

  return (
    <nav className={classes.nav} aria-label="Chat">
      <SidebarRow
        icon={<NavIcon icon="chat" />}
        label="New Chat"
        onClick={onNewChat}
        hoverHighlight={true}
        dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'new-chat'}}
      />
      <SidebarRow
        icon={<NavIcon icon="robot" />}
        label="Agents"
        onClick={() => {
          openSettings(ChatSettingsTab.AGENTS);
        }}
        hoverHighlight={true}
        dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'agents'}}
      />
      <SidebarRow
        icon={<NavIcon icon="magic-wand" />}
        label="Skills"
        onClick={() => {
          openSettings(ChatSettingsTab.SKILLS);
        }}
        hoverHighlight={true}
        dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'skills'}}
      />
      <SidebarRow
        icon={<NavIcon icon="integration" />}
        label="AI Connectors"
        onClick={() => {
          openSettings(ChatSettingsTab.CONNECTORS);
        }}
        hoverHighlight={true}
        dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'ai-connectors'}}
      />
    </nav>
  );
}
